import { Text } from '@/components/DesignSystem';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import {
  IconButton,
  SxProps,
  Table as MuiTable,
  TableCell,
  TableHead,
  TableProps as MuiTableProps,
  TableRow as MuiTableRow,
  TableRowProps as MuiTableRowProps,
  Theme,
  useTheme,
} from '@mui/material';
import { Children, ReactElement, ReactNode, useState } from 'react';

export interface TableProps extends Omit<MuiTableProps, 'headers'> {
  headers: (ReactNode | string)[],
  headerWidths?: (number | undefined)[],
  headerSx?: SxProps<Theme>,
  isFixed?: boolean,
  rows?: ReactNode[][],
}

export interface TableRowProps extends Omit<MuiTableRowProps, 'values'> {
  expandedComponent?: ReactNode,
  values: ReactNode[],
}

export const Table = ({
  children,
  headers,
  headerWidths,
  headerSx,
  rows,
  ...rest
}: TableProps) => {
  const theme = useTheme();
  const hasExpandableRow = Children.toArray(children).some((child) => (
    (child as ReactElement).props.expandedComponent
  ));

  return (
    <MuiTable {...rest} sx={{
      bgcolor: theme.palette.background.paper,
      tableLayout: headerWidths?.length ? 'fixed' : undefined,
    }}>
      <TableHead>
        <MuiTableRow>
          {hasExpandableRow && (
            <TableCell sx={{ borderBottom: `1px solid ${theme.palette.divider}`, width: '60px', ...headerSx }} />
          )}
          {headers.map((header, index) => (
            <TableCell
              key={index}
              sx={{
                borderBottom: `1px solid ${theme.palette.divider}`,
                ...headerSx,
              }}
              width={headerWidths?.[index] ? headerWidths[index] : 'auto'}
            >
              <Text category="label-large">{header}</Text>
            </TableCell>
          ))}
        </MuiTableRow>
      </TableHead>
      {children ?? rows?.map((row, i) => (
        <MuiTableRow key={i}>
          {row.map((value, j) => (
            <TableCell key={j}>
              {value}
            </TableCell>
          ))}
        </MuiTableRow>
      ))}
    </MuiTable>
  );
};

export const TableRow = ({
  expandedComponent,
  values,
  ...rest
}: TableRowProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const theme = useTheme();
  return (
    <>
      <MuiTableRow {...rest}>
        {!!expandedComponent && (
          <TableCell sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
            <IconButton onClick={() => setIsExpanded(!isExpanded)}>
              {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowRight />}
            </IconButton>
          </TableCell>
        )}
        {values.map((value, i) => (
          <TableCell key={i} sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
            {value}
          </TableCell>
        ))}
      </MuiTableRow>
      {isExpanded && expandedComponent}
    </>
  );
};
